import {
    HTMLAttributes,
    RefObject,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    FocusEvent
} from 'react';

export interface UseListKeyboardNavigationProps {
    /** Ref to the scrollable content container. */
    containerRef: RefObject<HTMLElement>;
    /** CSS selector for navigable items. @default '[data-list-item]' */
    itemSelector?: string;
    /** Whether arrow navigation wraps around. @default true */
    loop?: boolean;
    /** Called when the active item changes. */
    onActiveItemChange?: (element: HTMLElement, index: number) => void;
    /** Navigation axis. @default 'vertical' */
    orientation?: 'vertical' | 'horizontal';
    /**
     * Whether keyboard navigation is active.
     * When false, tabindex sync is skipped so external focus managers (e.g.
     * Radix dropdown) can manage tabindices without interference.
     * @default true
     */
    enabled?: boolean;
}

export interface UseListKeyboardNavigationReturn {
    /** Props to spread on the scrollable content container. */
    listContentProps: Pick<
        HTMLAttributes<HTMLElement>,
        'role' | 'tabIndex' | 'onKeyDown' | 'onFocus' | 'onBlur'
    > & { 'aria-orientation': 'vertical' | 'horizontal' };
    /** Index of the currently focused item (-1 if none). */
    activeIndex: number;
    /**
     * Imperatively move focus to the item at `index` within the navigable
     * (non-disabled) items list. Calls `.focus()` on the DOM element as a
     * side-effect — use this when you need to restore or programmatically
     * drive keyboard focus, not just track state.
     */
    focusItemAtIndex: (index: number) => void;
}

export const useListKeyboardNavigation = ({
    containerRef,
    itemSelector = '[data-list-item]',
    loop = true,
    onActiveItemChange,
    orientation = 'vertical',
    enabled = true
}: UseListKeyboardNavigationProps): UseListKeyboardNavigationReturn => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const activeIndexRef = useRef(activeIndex);
    activeIndexRef.current = activeIndex;

    // When an item has focus, the container must not be a tab stop — otherwise
    // Shift+Tab from an item lands on the container and handleFocus sends focus
    // straight back to the item. Restore to 0 when focus leaves so Tab can re-enter.
    const [containerTabIndex, setContainerTabIndex] = useState(0);

    const getItems = useCallback((): HTMLElement[] => {
        if (!containerRef.current) {
            return [];
        }
        return Array.from(
            containerRef.current.querySelectorAll<HTMLElement>(itemSelector)
        ).filter(item => item.getAttribute('data-disabled') !== 'true');
    }, [containerRef, itemSelector]);

    const focusItem = useCallback(
        (index: number) => {
            const items = getItems();
            if (index < 0 || index >= items.length) {
                return;
            }

            // Clear visual indicators from the previously active item
            const prevIdx = activeIndexRef.current;
            if (prevIdx >= 0 && prevIdx < items.length) {
                items[prevIdx].removeAttribute('data-hover');
                items[prevIdx].removeAttribute('data-focus-visible');
            }

            // Set visual indicators on the newly active item
            items[index].setAttribute('data-hover', '');
            items[index].setAttribute('data-focus-visible', '');

            setContainerTabIndex(-1);
            setActiveIndex(index);
            items[index].focus();
            items[index].scrollIntoView?.({ block: 'nearest' });
            onActiveItemChange?.(items[index], index);
        },
        [getItems, onActiveItemChange]
    );

    const getNextIndex = useCallback(
        (current: number, length: number): number => {
            if (current < length - 1) {
                return current + 1;
            }
            return loop ? 0 : current;
        },
        [loop]
    );

    const getPrevIndex = useCallback(
        (current: number, length: number): number => {
            if (current > 0) {
                return current - 1;
            }
            return loop ? length - 1 : current;
        },
        [loop]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const items = getItems();
            if (items.length === 0) {
                return;
            }

            const prevKey =
                orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
            const nextKey =
                orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';

            switch (e.key) {
                case nextKey: {
                    e.preventDefault();
                    const current =
                        activeIndexRef.current >= 0
                            ? activeIndexRef.current
                            : -1;
                    focusItem(getNextIndex(current, items.length));
                    break;
                }
                case prevKey: {
                    e.preventDefault();
                    const current =
                        activeIndexRef.current >= 0
                            ? activeIndexRef.current
                            : 0;
                    focusItem(getPrevIndex(current, items.length));
                    break;
                }
                case 'Home':
                    e.preventDefault();
                    focusItem(0);
                    break;
                case 'End':
                    e.preventDefault();
                    focusItem(items.length - 1);
                    break;
                case ' ':
                case 'Enter': {
                    e.preventDefault();
                    const idx = activeIndexRef.current;
                    if (idx >= 0 && idx < items.length) {
                        items[idx].click();
                    }
                    break;
                }
            }
        },
        [getItems, focusItem, getNextIndex, getPrevIndex, orientation]
    );

    const handleBlur = useCallback(
        (e: FocusEvent) => {
            // If focus is moving to another element inside the container, ignore
            if (containerRef.current?.contains(e.relatedTarget as Node)) {
                return;
            }

            // Focus left the list — clear visual indicators and restore container to tab order
            const items = getItems();
            const idx = activeIndexRef.current;
            if (idx >= 0 && idx < items.length) {
                items[idx].removeAttribute('data-hover');
                items[idx].removeAttribute('data-focus-visible');
            }
            setContainerTabIndex(0);
        },
        [containerRef, getItems]
    );

    const handleFocus = useCallback(
        (e: React.FocusEvent) => {
            // Only redirect when the container itself receives focus, not children
            if (e.target !== containerRef.current) {
                return;
            }

            // Safety net: if focus came from inside the container (Shift+Tab from an item
            // before the containerTabIndex state update has flushed to the DOM), don't
            // redirect — let focus sit on the container so the next Shift+Tab exits the list.
            if (containerRef.current?.contains(e.relatedTarget as Node)) {
                return;
            }

            const items = getItems();
            if (items.length === 0) {
                return;
            }

            const idx = activeIndexRef.current;
            const indexToFocus = idx >= 0 && idx < items.length ? idx : 0;
            focusItem(indexToFocus);
        },
        [containerRef, getItems, focusItem]
    );

    // Sync tabindex attributes on items after every render.
    // No dependency array is intentional: item count can change at any time
    // (e.g. search filtering) without activeIndex changing. Running after
    // every render guarantees newly-mounted items get the correct tabindex
    // without needing consumers to signal "items changed".
    // Guard: skip entirely when disabled so external focus managers (e.g.
    // Radix DropdownMenu) can manage tabindices without interference.
    useLayoutEffect(() => {
        if (!containerRef.current || !enabled) {
            return;
        }

        // Set all items (including disabled) to non-focusable by default
        containerRef.current
            .querySelectorAll<HTMLElement>(itemSelector)
            .forEach(item => item.setAttribute('tabindex', '-1'));

        // Give the active navigable (non-disabled) item tabindex 0
        const navigableItems = getItems();
        const safeIndex =
            activeIndex >= 0 && activeIndex < navigableItems.length
                ? activeIndex
                : 0;
        navigableItems[safeIndex]?.setAttribute('tabindex', '0');
    });

    return {
        listContentProps: {
            role: 'listbox',
            tabIndex: containerTabIndex,
            onKeyDown: handleKeyDown,
            onFocus: handleFocus,
            onBlur: handleBlur,
            'aria-orientation': orientation
        },
        activeIndex,
        focusItemAtIndex: focusItem
    };
};
